/*
  ─── A versão do app de loja em que a página roda ───

  O app da App Store e o da Play Store são a casca (o repo `webview`): um
  WebView que abre este site. O site se atualiza sozinho a cada deploy; a casca
  não, e é nela que mora o que o celular faz (Apple Saúde, Conexão Saúde,
  galeria, tela ligada no treino). Quem não atualiza a casca fica sem isso, e
  até 29/10/2026 ninguém sabia quem era: a casca não contava a versão para a
  página nem para a API (o user agent dela é fixo).

  Desde a casca que conta a versão, ela chega pronta em
  `__WELLTRAINER_NATIVE_VERSAO__`, antes do primeiro script da página. Para a
  casca de antes, a versão sai das marcas que cada uma injeta, e por isso é uma
  FAIXA, com o teto que ela pode ter:

  | Marcas da casca                         | Rótulo   | Pode ser até        |
  |-----------------------------------------|----------|---------------------|
  | galeria (`SAVE_IMAGES`), sem a versão   | `~1.4.1` | 1.4.2 (ver abaixo)  |
  | saúde com a plataforma, sem a galeria   | `~1.4.0` | 1.4.0               |
  | só a saúde (iPhone)                     | `~1.3`   | 1.3.1               |
  | nenhuma, no iPad                        | `<1.4.1` | 1.4.0 (sem HealthKit, o iPad não ganha a marca da saúde) |
  | nenhuma, no iPhone                      | `<1.3`   | 1.2.x               |
  | nenhuma, no Android                     | `<1.4`   | 1.3.x               |

  O `~` diz "deduzida" e separa no banco quem conta a versão de quem não conta.

  O teto da galeria é 1.4.2, e não 1.4.1, de propósito: a marca da versão foi
  escrita depois de a 1.4.2 estar pronta, e se a 1.4.2 da loja sair sem ela,
  quem a instalou cai nesta linha. Com o teto em 1.4.1, essa pessoa seria
  mandada atualizar estando em dia, até a próxima versão.

  O teto cai sozinho quando a API PROVA a marca: a `primeira_versao_que_conta`
  do `GET /app-config` é a menor versão exata que pelo menos 3 pessoas mandaram.
  Quem não conta a versão está antes dela, então com a loja nessa versão ou
  depois, está atrás. Com a 1.4.2 da loja contando a versão, quem ficou na 1.4.1
  passa a ver o aviso no dia em que 3 pessoas aparecerem na 1.4.2, sem ninguém
  mexer em nada.

  A regra: a casca está atrás quando a versão da loja (`versao_na_loja`) passa do
  TETO dela, ou, para quem não conta a versão, quando chega à primeira versão
  que conta. Na dúvida, ninguém é mandado à loja.

  O rótulo vai no cabeçalho `X-App-Versao` (`lib/api.ts`), e a API guarda em
  `usuario.versao_app_ios` ou `versao_app_android` quando muda: é o que responde
  quem está com o app velho.
*/

declare global {
  interface Window {
    /** A versão da casca ("1.4.2"), desde a casca que a conta. Existe antes do primeiro script. */
    __WELLTRAINER_NATIVE_VERSAO__?: string
    /** A casca salva imagem direto na galeria (desde a 1.4.1). */
    __WELLTRAINER_NATIVE_SAVE_IMAGES__?: boolean
  }
}

export type SistemaDoApp = "ios" | "android"

export type VersaoDaCasca = {
  sistema: SistemaDoApp
  /** A versão exata, ou a deduzida ("~1.4.1", "~1.3", "<1.3"). É o que vai à API. */
  rotulo: string
  /** A maior versão que esta casca pode ser. */
  teto: number[]
  /** A casca contou a versão (a marca `__WELLTRAINER_NATIVE_VERSAO__`). */
  exata: boolean
}

const FORMATO = /^\d{1,4}(\.\d{1,4}){1,3}$/

/** "1.4.1" em [1, 4, 1]; `null` fora do formato. */
export function partesDaVersao(versao: unknown): number[] | null {
  if (typeof versao !== "string" || !FORMATO.test(versao.trim())) return null
  return versao.trim().split(".").map(Number)
}

/** Negativo se `a` vem antes, zero se iguais ("1.4" e "1.4.0" são iguais), positivo se depois. */
export function compararVersoes(a: number[], b: number[]): number {
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const diferenca = (a[i] ?? 0) - (b[i] ?? 0)
    if (diferenca !== 0) return diferenca
  }
  return 0
}

/**
 * A versão da casca em que a página roda, ou `null` fora dela (navegador) e
 * enquanto a casca não terminou de pôr as marcas.
 *
 * O sistema vem da marca `__WELLTRAINER_NATIVE_PLATFORM__`, que a casca põe no
 * FIM do carregamento, junto com a da galeria e no mesmo script: com o sistema
 * na página, a galeria já está decidida. Antes dele, `null`, e quem pergunta
 * tenta de novo (`useVersaoDaCasca`).
 */
export function versaoDaCasca(): VersaoDaCasca | null {
  if (typeof window === "undefined") return null
  const sistema = window.__WELLTRAINER_NATIVE_PLATFORM__
  if (sistema !== "ios" && sistema !== "android") return null

  const exata = partesDaVersao(window.__WELLTRAINER_NATIVE_VERSAO__)
  if (exata) return { sistema, rotulo: exata.join("."), teto: exata, exata: true }

  const deduzida = (rotulo: string, teto: number[]): VersaoDaCasca => ({ sistema, rotulo, teto, exata: false })
  if (window.__WELLTRAINER_NATIVE_SAVE_IMAGES__ === true) return deduzida("~1.4.1", [1, 4, 2])
  if (window.__WELLTRAINER_NATIVE_SAUDE_PLATAFORMA__) return deduzida("~1.4.0", [1, 4, 0])
  if (sistema === "android") return deduzida("<1.4", [1, 3, 9999])
  if (window.__WELLTRAINER_NATIVE_SAUDE__ === true) return deduzida("~1.3", [1, 3, 1])
  /* A casca do iPad usa o user agent do iPad (`isTablet` na casca). */
  if (/iPad/.test(navigator.userAgent)) return deduzida("<1.4.1", [1, 4, 0])
  return deduzida("<1.3", [1, 2, 9999])
}

/**
 * A casca está atrás da loja? Quando a loja passa do teto dela, ou, para a casca
 * que não conta a versão, quando a loja chega à primeira versão que conta (a
 * casca que não conta está antes dela).
 */
export function cascaAtrasDaLoja(
  casca: VersaoDaCasca,
  naLoja: string | null | undefined,
  primeiraQueConta?: string | null,
): boolean {
  const loja = partesDaVersao(naLoja)
  if (!loja) return false
  if (compararVersoes(loja, casca.teto) > 0) return true
  const marca = partesDaVersao(primeiraQueConta)
  return !casca.exata && !!marca && compararVersoes(loja, marca) >= 0
}
